import { useEffect, useState } from 'react'
import SiteFooter from '../components/SiteFooter'
import SiteHeader from '../components/SiteHeader'
import s from './Agents.module.css'

type Agent = {
  code: string
  name: string
  short: string
  desc: string
  tags: string[]
  icon: string
  note?: string
}

const AGENTS: Agent[] = [
  {
    code: 'OPS',
    name: '운영',
    short: '조율자',
    desc: '전체를 조율합니다. 작업이 들어오면 그룹방을 만들고, 누가 말할 차례인지 정하고, 회의를 진행해 결과를 한곳에 모읍니다.',
    tags: ['그룹방 생성', '발언자 선정', '회의 진행', '결과 취합'],
    icon: 'ico-checktile',
  },
  {
    code: 'EXEC',
    name: '경영',
    short: '판단',
    desc: '요청이 무엇인지부터 정리합니다. MVP 범위를 긋고 우선순위를 매긴 뒤 진행할지 말지를 판단합니다.',
    tags: ['요청 이해', 'MVP 범위', '우선순위', 'Go/No-Go'],
    icon: 'ico-bars',
  },
  {
    code: 'PM',
    name: '기획',
    short: '명세',
    desc: '화면과 유저플로우를 그리고, 무엇을 만족해야 완료로 볼지 수락 기준을 문서로 남깁니다.',
    tags: ['화면 설계', '유저플로우', '수락 기준 명세'],
    icon: 'ag-clipboard',
  },
  {
    code: 'DESIGN',
    name: '디자인',
    short: 'UI · 톤 · 카피',
    desc: 'UI 방향과 톤을 정하고 카피를 다듬습니다. 필요한 이미지가 무엇인지도 명세로 넘깁니다.',
    tags: ['UI 방향', '톤 & 매너', '카피', '이미지 명세'],
    icon: 'ico-spark',
  },
  {
    code: 'DEV',
    name: '개발',
    short: 'FE · BE 코드',
    desc: '프론트엔드와 백엔드 코드를 씁니다. 결과는 미리보기로 확인하고 zip으로 받아볼 수 있습니다.',
    tags: ['FE 코드', 'BE 코드', 'preview', 'zip 내보내기'],
    icon: 'ico-bolt',
  },
  {
    code: 'Finance',
    name: '총무',
    short: '매출 · 지표',
    desc: '매출과 지표를 읽어 설명합니다. 재무에 가까운 일을 하지만 딱딱하지 않게 풀어 전달합니다.',
    tags: ['매출 설명', '지표 해석', '재무 요약'],
    icon: 'ag-chart',
  },
  {
    code: 'MKT',
    name: '마케팅',
    short: '카피',
    desc: '상품 소개, 랜딩 페이지, SNS에 쓸 카피를 씁니다. 채널에 맞는 어투로 맞춰 냅니다.',
    tags: ['상품 카피', '랜딩 카피', 'SNS 카피'],
    icon: 'ag-megaphone',
  },
  {
    code: 'CS',
    name: 'CS',
    short: '응대 문구',
    desc: '자주 묻는 질문과 문의 응대 문구를 만듭니다. 같은 질문에 매번 답을 새로 쓰지 않아도 됩니다.',
    tags: ['FAQ 작성', '문의 응대 문구'],
    icon: 'ag-user',
  },
  {
    code: 'LEGAL',
    name: '법무',
    short: '문서 초안',
    desc: '약관, 개인정보 처리방침, 환불 정책 초안을 작성합니다.',
    tags: ['약관 초안', '개인정보 초안', '환불 정책 초안'],
    icon: 'ico-shield',
    note: '※ 초안 작성만 지원하며 법률 자문이 아닙니다. 시행 전 전문가 검토가 필요합니다.',
  },
]

/** 제목 아래에서 슬롯처럼 굴러가는 문구. 늘리고 싶으면 여기에 줄만 추가하면 된다. */
const LINES = [
  '9개 역할이 한 팀처럼 움직입니다.',
  '왼쪽 이름을 누르면 오른쪽에 설명창이 뜹니다.',
]

export default function Agents() {
  // 처음에는 아무것도 안 고른 상태 — 왼쪽을 눌러야 오른쪽이 켜진다
  const [pick, setPick] = useState<number | null>(null)
  const sel = pick === null ? null : AGENTS[pick]

  const [line, setLine] = useState(0)
  useEffect(() => {
    // 움직이는 글자는 모션 최소화 설정에서 멈춰야 한다 — 첫 줄만 남는다
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return
    // 넘어가는 주기(ms). 미끄러지는 속도는 Agents.module.css의 .rollIn transition
    const t = setInterval(() => setLine((v) => (v + 1) % LINES.length), 9000)
    return () => clearInterval(t)
  }, [])

  return (
    <div className={s.page}>
      <SiteHeader current="#/agents" />

      <div className={s.body}>
        <div className={s.wrap}>
          <div className={`${s.head} enter`}>
            <h1>AI 에이전트 소개</h1>
            {/* 세로로 굴러가는 슬롯. 줄이 전부 DOM에 있어 스크린리더는 그대로 다 읽는다 */}
            <div className={s.roll}>
              <div
                className={s.rollIn}
                style={{ transform: `translateY(calc(var(--lh) * ${-line}))` }}
              >
                {/* 같은 문구가 두 번 들어가도 깨지지 않게 key는 인덱스로 (순서가 바뀌지 않는 고정 목록) */}
                {LINES.map((l, i) => (
                  <p key={i}>{l}</p>
                ))}
              </div>
            </div>
          </div>

          <div className={`${s.split} enter enter-1`} data-open={sel !== null}>
            {/* 탭 패턴 — 좌우 어느 쪽이 먼저인지 스크린리더도 알 수 있게 role 부여 */}
            <ul className={s.list} role="tablist" aria-label="AI 에이전트 목록">
              {AGENTS.map((a, i) => (
                <li key={a.code}>
                  <button
                    type="button"
                    role="tab"
                    id={`ag-t${i}`}
                    aria-selected={i === pick}
                    aria-controls="ag-panel"
                    onClick={() => setPick(i)}
                  >
                    {a.name}
                  </button>
                </li>
              ))}
            </ul>

            {/* 고르기 전에는 아예 그리지 않는다. key로 교체될 때마다 등장 연출이 재생된다 */}
            {sel && (
              <div
                className={s.detail}
                id="ag-panel"
                role="tabpanel"
                aria-labelledby={`ag-t${pick}`}
                key={sel.code}
              >
                <img className={s.icon} src={`/assets/${sel.icon}.png`} alt="" />
                <span className={s.code}>{sel.code}</span>
                <h2>{sel.name}</h2>
                <p>{sel.desc}</p>

                <h3>하는 일</h3>
                <ul className={s.did}>
                  {sel.tags.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>

                {sel.note && <p className={s.note}>{sel.note}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  )
}
